import type {
  AdminOrganizationDetail,
  ApplicationWithApplicant,
  CompanyDetail,
  MemberDetail,
  UseCaseDetail,
} from "@kansai-ai-hub/api-client";

import { adminOrganizationQueryOptions } from "@/features/admin/queries";
import { applicationDetailQueryOptions } from "@/features/applications/queries";
import { caseDetailQueryOptions } from "@/features/cases/queries";
import { companyDetailQueryOptions } from "@/features/companies/queries";
import { memberDetailQueryOptions } from "@/features/enterprise/queries";

import {
  buildCaseSectionTrail,
  buildMemberSectionTrail,
  resolveEnterpriseSection,
} from "./case-origin";
import type { CrumbDef } from "./crumb-types";
import { STATIC_LABELS } from "./nav-registry";

/**
 * `$word` 形式のパラメータをパス文字列に埋め込むユーティリティ。
 * fallback (`?? \`$${name}\``) が発火するのは crumb 定義側のテンプレートが間違っている時
 * （例: 親階層に存在しない param を参照）。dev mode では明示的に warn して気付けるようにする。
 *
 * NOTE: `params.id` / `params.appId` の直参照は、ファイルベースルーティングの命名規約
 * （`$id` / `$appId`）に依存している。ルートで使うパラメータ名を変えた場合はここも同期する。
 *
 * 正規表現 `\w` は `[A-Za-z0-9_]` をマッチ。route params 名は ASCII identifier のみサポート。
 * 漢字/日本語の param 名は使わない前提。
 *
 * NOTE 2: 各 resolver は **pure read only**。`queryClient.setQueryData` 等の write は禁止
 * (render 中の副作用を避ける)。cache update は loader / mutation で行うこと。
 */
export function substituteParams(
  template: string,
  params: Record<string, string | undefined>,
): string {
  return template.replace(/\$(\w+)/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) {
      if (import.meta.env.DEV) {
        console.warn(
          `[crumb-resolvers] substituteParams: param "${name}" not found in template "${template}". ` +
            `Falling back to literal "$${name}". Check the crumb definition.`,
        );
      }
      return `$${name}`;
    }
    return value;
  });
}

/** 詳細データ未取得時に ID 文脈を残す placeholder ラベルを作る */
function fallbackIdLabel(prefix: string, id: string): string {
  const short = id.length > 8 ? `${id.slice(0, 8)}…` : id;
  return `${prefix} ${short}`;
}

/**
 * `$id` を持つ案件詳細レイヤー用。template に `$id` を含めると現在の params で差し替える。
 *
 * - cache hit: 案件タイトル + 親 URL への anchor
 * - cache miss: 「案件 abc12345…」の fallback ラベル **のみ** (to なし)
 *   → AutoBreadcrumb は to なし crumb をリンクではなく灰色 Text で描画する
 *     (祖先位置なら情報として、末尾なら現在地として表示)
 *   → 壊れた URL に anchor を貼って 404 ループになるのを防ぎつつ ID 文脈は残す
 */
export const caseTitleCrumb = (toTemplate?: string): CrumbDef => ({
  resolve: ({ params, queryClient }) => {
    const id = params.id;
    if (!id) return null;
    const data = queryClient.getQueryData<UseCaseDetail>(
      caseDetailQueryOptions(id).queryKey,
    );
    if (!data) {
      return { label: fallbackIdLabel("案件", id) };
    }
    return {
      label: data.title,
      to: toTemplate ? substituteParams(toTemplate, params) : undefined,
    };
  },
});

/**
 * admin 案件まわりの layout (`/admin/cases`) 用の祖先 crumb。
 *
 * 案件詳細はツリー上 `全案件管理` の配下に固定だが、実際は複数 L1 から到達する。
 * AppLayout が捕捉した出自 (`originL1` / `originOrg`) から祖先トレイルを組み立てる:
 * - 組織詳細を経由 → `組織一覧 › 組織名` の2階層 (配列で返す)
 * - 承認待ち案件 / 応募管理 / 全案件管理 などの L1 → そのセクション1つ
 * - cold load → 全案件管理 にフォールバック
 *
 * 一覧ページ (`/admin/cases` 自身) は L1 なので AutoBreadcrumb 側で非表示になり、
 * ここでの分岐結果は使われない。
 */
export const casesOriginCrumb = (): CrumbDef => ({
  resolve: ({ originL1, originOrg }) => buildCaseSectionTrail(originL1, originOrg),
});

/**
 * 企業ロールの `/enterprise` layout 用の祖先 crumb。
 *
 * 案件詳細 (`/enterprise/$id...`) はツリー上 layout 直下に固定だが、実際は
 * ダッシュボード / 承認待ち / 承認後案件 / 応募管理 から到達する。出自 L1 を根セクションへ寄せる。
 * L1 一覧ページ自身では AutoBreadcrumb が非表示にするので、分岐結果は詳細でのみ効く。
 */
export const enterpriseOriginCrumb = (): CrumbDef => ({
  resolve: ({ originL1 }) => resolveEnterpriseSection(originL1),
});

/**
 * 会員ロールの `/member` layout 用の祖先 crumb。
 *
 * `/member` layout は **案件サブツリー (`/member/$id...`)** と **企業サブツリー
 * (`/member/companies...`)** の両方の親。出自セクションを出したいのは案件サブツリーだけで、
 * 企業サブツリーでは companies layout が `企業一覧` を出すので、ここでは出さない (null)。
 *
 * 案件サブツリーでは出自からトレイルを組む:
 * - 企業詳細を経由 → `企業一覧 › 企業名` の2階層
 * - 案件一覧 / マイ応募 / 企業一覧(直) → そのセクション1つ (cold load は 案件一覧)
 */
export const memberLayoutCrumb = (): CrumbDef => ({
  resolve: ({ originL1, originOrg, pathname }) => {
    if (pathname && pathname.startsWith("/member/companies")) return null;
    return buildMemberSectionTrail(originL1, originOrg);
  },
});

/**
 * `$appId` を持つ応募詳細用。末端要素なので `to` は持たない。
 * 応募は「団体（応募元組織）」が主体なので、見出し（PageHeader）と揃えて
 * 担当者名ではなく応募元組織名を出す。
 */
export const applicantNameCrumb = (): CrumbDef => ({
  resolve: ({ params, queryClient }) => {
    const appId = params.appId;
    if (!appId) return null;
    const data = queryClient.getQueryData<ApplicationWithApplicant>(
      applicationDetailQueryOptions(appId).queryKey,
    );
    if (!data) {
      return { label: fallbackIdLabel("応募", appId) };
    }
    return { label: data.organizationName };
  },
});

/** enterprise の `/enterprise/members/$id`、末端要素（応募詳細から開く会員詳細）。 */
export const memberOrgNameCrumb = (): CrumbDef => ({
  resolve: ({ params, queryClient }) => {
    const id = params.id;
    if (!id) return null;
    const data = queryClient.getQueryData<MemberDetail>(
      memberDetailQueryOptions(id).queryKey,
    );
    if (!data) {
      return { label: fallbackIdLabel("会員", id) };
    }
    return { label: data.name };
  },
});

/** member の `/member/companies/$id`、末端要素。 */
export const companyNameCrumb = (): CrumbDef => ({
  resolve: ({ params, queryClient }) => {
    const id = params.id;
    if (!id) return null;
    const data = queryClient.getQueryData<CompanyDetail>(
      companyDetailQueryOptions(id).queryKey,
    );
    if (!data) {
      return { label: fallbackIdLabel("企業", id) };
    }
    return { label: data.name };
  },
});

/** admin の `/admin/organizations/$id`、末端要素。 */
export const organizationNameCrumb = (): CrumbDef => ({
  resolve: ({ params, queryClient }) => {
    const id = params.id;
    if (!id) return null;
    const data = queryClient.getQueryData<AdminOrganizationDetail>(
      adminOrganizationQueryOptions(id).queryKey,
    );
    if (!data) {
      return { label: fallbackIdLabel("組織", id) };
    }
    return { label: data.name };
  },
});

/**
 * `/admin/organizations` layout 用の crumb。
 * leaf の `$id` から organization の role を読んで、戻り先タブ (companies / members) を
 * 動的に切り替える。leaf 不在 / cache miss 時は安全側で companies に倒す。
 */
export const organizationsLayoutCrumb = (): CrumbDef => ({
  resolve: ({ allParams, queryClient }) => {
    const orgId = allParams.id;
    if (orgId) {
      const data = queryClient.getQueryData<AdminOrganizationDetail>(
        adminOrganizationQueryOptions(orgId).queryKey,
      );
      if (data) {
        const tab = data.role === "ENTERPRISE" ? "companies" : "members";
        return { label: STATIC_LABELS.adminOrganizations, to: `/admin/organizations/${tab}` };
      }
    }
    return {
      label: STATIC_LABELS.adminOrganizations,
      to: "/admin/organizations/companies",
    };
  },
});
